import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["invalid", "classNamePrefix", "simpleValue", "isMulti", "pluckSingleValue", "options", "loadOptions", "loadingMessage", "loadingProps", "selectVariant", "updatingMessage", "noOptionsMessage", "value", "onChange", "loadOptionsChangeCounter", "SelectComponent", "noValueUpdates", "optionsTransformer", "compareValues"];

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useSelect from '../use-select/use-select';
import deepEqual from './deep-equal';

var Select = function Select(_ref) {
  var invalid = _ref.invalid,
      classNamePrefix = _ref.classNamePrefix,
      simpleValue = _ref.simpleValue,
      isMulti = _ref.isMulti,
      pluckSingleValue = _ref.pluckSingleValue,
      propsOptions = _ref.options,
      loadOptions = _ref.loadOptions,
      loadingMessage = _ref.loadingMessage,
      loadingProps = _ref.loadingProps,
      selectVariant = _ref.selectVariant,
      updatingMessage = _ref.updatingMessage,
      noOptionsMessage = _ref.noOptionsMessage,
      value = _ref.value,
      onChange = _ref.onChange,
      loadOptionsChangeCounter = _ref.loadOptionsChangeCounter,
      SelectComponent = _ref.SelectComponent,
      noValueUpdates = _ref.noValueUpdates,
      optionsTransformer = _ref.optionsTransformer,
      _ref$compareValues = _ref.compareValues,
      compareValues = _ref$compareValues === void 0 ? deepEqual : _ref$compareValues,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useSelect = useSelect({
    loadOptions: loadOptions,
    optionsTransformer: optionsTransformer,
    options: propsOptions,
    noValueUpdates: noValueUpdates,
    onChange: onChange,
    value: value,
    loadOptionsChangeCounter: loadOptionsChangeCounter,
    isSearchable: props.isSearchable,
    pluckSingleValue: pluckSingleValue,
    isMulti: isMulti,
    simpleValue: simpleValue,
    compareValues: compareValues
  }),
      state = _useSelect.state,
      selectValue = _useSelect.value,
      selectOnChange = _useSelect.onChange,
      onInputChange = _useSelect.onInputChange,
      isFetching = _useSelect.isFetching;

  var renderNoOptionsMessage = function renderNoOptionsMessage() {
    return Object.values(state.promises).some(function (value) {
      return value;
    }) ? function () {
      return updatingMessage;
    } : function () {
      return noOptionsMessage;
    };
  }; // When isMulti is true, the "getSelect" always creates new value array, we need to memoize it to not create new array instance
  // Memo is required to fix https://github.com/data-driven-forms/react-forms/issues/1366
  // Keeping prev values in ref and calling lodash.isEqual is not reliable as it ca return false positive beucase it only has true/false result.
  // If we have multiple updates during one reconciliation pahse the search input reset will trigger on initial key stroke
  // JSON.stringify is expensive but seems to be working better.


  var selectValueInternal = useMemo(function () {
    return selectValue;
  }, [JSON.stringify(selectValue)]);

  if (state.isLoading) {
    return /*#__PURE__*/React.createElement(SelectComponent, _extends({}, props, {
      classNamePrefix: classNamePrefix,
      isDisabled: true,
      isFetching: true,
      placeholder: loadingMessage,
      options: state.options,
      onChange: function onChange() {},
      onInputChange: onInputChange,
      value: selectValueInternal,
      isMulti: isMulti
    }, loadingProps, {
      noOptionsMessage: renderNoOptionsMessage()
    }, state.originalOptions && {
      originalOptions: state.originalOptions
    }));
  }

  return /*#__PURE__*/React.createElement(SelectComponent, _extends({
    className: clsx(classNamePrefix, {
      'has-error': invalid
    })
  }, props, {
    isDisabled: props.isDisabled || props.isReadOnly,
    options: state.options,
    classNamePrefix: classNamePrefix,
    isMulti: isMulti,
    value: selectValueInternal,
    onChange: selectOnChange,
    onInputChange: onInputChange,
    isFetching: isFetching,
    noOptionsMessage: renderNoOptionsMessage(),
    hideSelectedOptions: false,
    closeMenuOnSelect: !isMulti
  }, state.originalOptions && {
    originalOptions: state.originalOptions
  }));
};

Select.propTypes = {
  options: PropTypes.array,
  onChange: PropTypes.func,
  classNamePrefix: PropTypes.string,
  invalid: PropTypes.bool,
  simpleValue: PropTypes.bool,
  isMulti: PropTypes.bool,
  pluckSingleValue: PropTypes.bool,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  loadOptionsChangeCounter: PropTypes.number,
  isDisabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  loadOptions: PropTypes.func,
  loadingMessage: PropTypes.node,
  loadingProps: PropTypes.object,
  selectVariant: PropTypes.string,
  updatingMessage: PropTypes.node,
  noOptionsMessage: PropTypes.node,
  isSearchable: PropTypes.bool,
  SelectComponent: PropTypes.elementType.isRequired,
  noValueUpdates: PropTypes.bool,
  optionsTransformer: PropTypes.func,
  compareValues: PropTypes.func
};
Select.defaultProps = {
  options: [],
  invalid: false,
  simpleValue: true,
  pluckSingleValue: true,
  placeholder: 'Choose...',
  isSearchable: false,
  isClearable: false,
  compareValues: deepEqual
};
export default Select;